/**
 * TypeScript view of the `fifteen_puzzle` format.
 * Source of truth: `shared/schema/fifteen_puzzle.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export interface FifteenLimits {
  maxMoves?: number | null;
}

export interface FifteenDefinition {
  puzzleType: "fifteen_puzzle";
  typeVersion: number;
  /** Side of the tray, 3 to 6. */
  size: number;
  /** size² entries in reading order, a permutation of 0..size²-1; 0 is the blank. */
  tiles: number[];
  limits?: FifteenLimits;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface FifteenProgress {
  version: 1;
  /** The slides so far. Replaying them from the definition gives the tray. */
  moves: string;
  /** The tail taken back, still redoable. Never sent to the server. */
  undone?: string;
}

export interface FifteenSubmission {
  moves: string;
}

/** The author's reference sequence of slides, in the submission format. */
export type FifteenSolution = FifteenSubmission;

export const BLANK = 0;
export const MIN_SIZE = 3;
export const MAX_SIZE = 6;
export const MAX_MOVES = 10000;

/** The direction a *tile* travels into the blank. The blank goes the other way. */
export type Direction = "u" | "d" | "l" | "r";
export const DIRECTIONS: Direction[] = ["u", "d", "l", "r"];

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
